import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scrolls the window to the top whenever the React Router pathname
 * changes. Without this, every <Link> navigation preserves the
 * previous scroll position — so e.g. clicking a footer link on a
 * long page lands the visitor at the bottom of the next page.
 *
 * Behaviour:
 *   • PUSH / REPLACE navigations → scroll to (0, 0)
 *   • POP navigations (browser back/forward) → leave scroll alone
 *     so the browser can restore the prior position
 *   • Hash anchors (`/page#section`) → skip the reset so the
 *     browser scrolls to the anchor target
 *   • Pure search-string changes on the same path → skip (filters,
 *     query params, tabs etc.)
 *
 * Mounted once at the root of <BrowserRouter>. Returns null.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === "POP") return;
    if (hash) return;
    // `instant` keeps the UX snappy on long pages.
    // `'auto'` is the legacy alias and works in older browsers.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navType]);

  return null;
}
